import { defineComponent as _defineComponent } from 'vue'
import { ObjectType, Offer, Offer as OfferGraphql, PromotedBundleEdge, SraEventAction } from '@/@types/graphql-types';
import { ImpressionTrackingEvents } from '@/enums/events';
import { useBackendTracking } from '@/helpers/composables/useBackendTracking';
import { useI18n } from '@/helpers/composables/useI18n';
import { getBuyboxCategoryByMonetizationGraphql } from '@/helpers/offer-helper';
import {
	SnowplowClickoutContextGraphql,
	SnowplowContext,
	SnowplowTitleContextGraphql,
} from '@/helpers/tracking/providers';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { BuyboxOfferCategory } from '@/interfaces/buybox';
import type { TitleDetail } from '@/interfaces/title-details-graphql';
import { SponsoredAdFragment } from '@/features/sponsored-recommendations/graphql/fragments/SponsoredAd.fragment';
import { NewBuyBoxView } from '@/stores/useUserStoreTypes';
import { computed, provide } from 'vue';
import { TitleOfferFragment } from '@/features/buybox/graphql/fragments/Offer.fragment';

import { useProvideClickoutPlacement } from '@/helpers/composables/useProvideClickoutPlacement';
import { useJWTVCampaign } from '@/features/jwtv-titles-rental/composables/useJWTVCampaign';
import GridOfferRowRedesignExp from '@/components/experiments/GridBuyboxRedesignExps/components/GridOfferRowRedesignExp.vue';
import GridOfferMoreInfoExp from '@/components/experiments/GridBuyboxRedesignExps/components/GridOfferMoreInfoExp.vue';
import GridPromotedOfferCardMoreInfoV1 from '@/components/experiments/GridBuyboxRedesignExps/components/GridPromotedOfferCardMoreInfoV1.vue';
import BuyBoxTypeSwitcher from '@/features/buybox/components/BuyBoxTypeSwitcher.vue';
import { useExperiment } from '@/libraries/experiment';

type BuyboxRowOffer = TitleOfferFragment & {
	iconUrl?: string;
	icons?: string[];
	bundleId?: number;
	bundleTechnicalName?: string;
	bundlePackageShortNames?: string[];
};

type OfferCategories =
	| BuyboxOfferCategory.STREAM
	| BuyboxOfferCategory.RENT
	| BuyboxOfferCategory.BUY
	| BuyboxOfferCategory.BUNDLE;


export default /*#__PURE__*/_defineComponent({
  __name: 'GridPromotedBuyboxRedesignExp',
  props: {
    title: null,
    titleId: null,
    titleObjectType: null,
    titleObjectId: null,
    promotedOffers: { default: () => [] },
    promotedBundles: { default: () => [] },
    sponsoredAd: { default: undefined },
    showDetails: { default: null },
    seasonNumber: { default: null },
    episodeNumber: { default: null },
    onlyBundle: { type: Boolean, default: false },
    isMiddleBuyBox: { type: Boolean, default: false },
    buyBoxSwitchType: null,
    additionalContexts: { default: () => [] }
  },
  setup(__props: any) {

const props = __props as { title: TitleDetail, titleId: string, titleObjectType: ObjectType, titleObjectId: number, promotedOffers: TitleOfferFragment[], promotedBundles: PromotedBundleEdge[], sponsoredAd: SponsoredAdFragment | undefined, showDetails: {
			id: string;
			objectId: number;
			objectType: ObjectType;
			title: string;
		} | null, seasonNumber: number | null, episodeNumber: number | null, onlyBundle: boolean, isMiddleBuyBox: boolean, buyBoxSwitchType?: NewBuyBoxView, additionalContexts: SnowplowContext[] };

const GridBundleRedesignExp = () =>
	import('@/components/experiments/GridBuyboxRedesignExps/components/GridBundleRedesignExp.vue');
const CategoryOrder = [
	BuyboxOfferCategory.STREAM,
	BuyboxOfferCategory.RENT,
	BuyboxOfferCategory.BUY,
	BuyboxOfferCategory.BUNDLE,
];



const { t } = useI18n();

useProvideClickoutPlacement('promoted_buybox');

provide('isMiddleBuyBox', () => props.isMiddleBuyBox);
provide('titleSourceId', props.titleId);

const { activeVariant: gridMoreInfoVariant } = useExperiment('EXP_GRID_MORE_INFO_REDESIGN_V2');

const { getPromotedOffersWithJwtv } = useJWTVCampaign();

const allPromotedOffers = computed(() => getPromotedOffersWithJwtv(props.promotedOffers, props.title));
const hasPromotedOffers = computed(
	() => allPromotedOffers.value.concat(props.promotedBundles.map(el => el.offer)).length > 0
);

const promotedOffersV1 = computed(() => {
	const offers = allPromotedOffers.value;
	const bundleOffers: BuyboxRowOffer[] = props.promotedBundles.map(b => ({
		...b.offer,
		standardWebURL: b.promotionUrl,
		bundleId: b.node.bundleId,
		bundleTechnicalName: b.node.technicalName,
		icons: b.node.packages.map(pkg => pkg.icon),
		bundlePackageShortNames: b.node.packages.map(pkg => pkg.shortName),
	}));
	return [...offers, ...bundleOffers].slice(0, 3) as BuyboxRowOffer[];
});

const categoryTranslatables = computed(() => {
	return {
		[BuyboxOfferCategory.STREAM]: t('WEBAPP_STREAM'),
		[BuyboxOfferCategory.RENT]: t('WEBAPP_RENT'),
		[BuyboxOfferCategory.BUY]: t('WEBAPP_BUY'),
		[BuyboxOfferCategory.BUNDLE]: t('WEBAPP_BUNDLES'),
	};
});

const categories = computed<OfferCategories[]>(() => {
	if (props.onlyBundle) {
		return [BuyboxOfferCategory.BUNDLE];
	}

	const promotedBundleOffers = props.promotedBundles.map(bundle => bundle.offer);

	const updatedOffers = allPromotedOffers.value
		.concat(promotedBundleOffers)
		.map(offer => getBuyboxCategoryByMonetizationGraphql(offer.monetizationType))
		.filter(key => key !== undefined)
		.filter((item, index, offers) => offers.indexOf(item) === index) as OfferCategories[];

	updatedOffers.sort((a, b) => CategoryOrder.indexOf(a) - CategoryOrder.indexOf(b));

	return updatedOffers;
});

function getOfferCategoryLabel(offer: TitleOfferFragment): string {
	const category = getBuyboxCategoryByMonetizationGraphql(offer.monetizationType);
	if (!category) return '';
	return (categoryTranslatables.value as Partial<Record<BuyboxOfferCategory, string>>)[category] ?? '';
}

function clickOutV4(payload: { buyboxOffer: TitleOfferFragment; isBundle: boolean }) {
	clickOut({ buyboxOffer: payload.buyboxOffer as unknown as OfferGraphql, isBundle: payload.isBundle });
}

function getBundlesByCategory(category: BuyboxOfferCategory): PromotedBundleEdge[] {
	return props.promotedBundles.filter(
		bundle => getBuyboxCategoryByMonetizationGraphql(bundle.offer.monetizationType) === category
	);
}

function getOffersByCategory(category: BuyboxOfferCategory): BuyboxRowOffer[] {
	return allPromotedOffers.value.filter(
		offer => getBuyboxCategoryByMonetizationGraphql(offer.monetizationType) === category
	);
}

const { trackSraEvent } = useBackendTracking();

function clickOut(payload: { buyboxOffer: OfferGraphql; isBundle: boolean }) {
	const sponsoredPackage = props.sponsoredAd?.campaign?.watchNowOffer.package;

	if (props.sponsoredAd?.bidId && payload.buyboxOffer.package.packageId === sponsoredPackage?.packageId) {
		const clickoutContext = SnowplowClickoutContextGraphql.fromProviderOffer(payload.buyboxOffer as Offer);
		const contexts = (props.additionalContexts || []).concat([
			new SnowplowTitleContextGraphql(
				props.titleObjectId,
				props.titleObjectType,
				undefined,
				undefined,
				undefined,
				undefined,
				props.title.id
			),
		]);
		contexts.push(clickoutContext);

		TrackingHelper.trackEvent(
			ImpressionTrackingEvents.SPONSORED_RECOMMENDATIONS,
			{
				action: 'buybox_clickout',
				label: `${props.titleId}_${sponsoredPackage.packageId}`,
				property: 'buybox',
				value: props.sponsoredAd?.holdoutGroup ? 0 : 1,
			},
			contexts
		);

		// track influenced campaign metrics
		trackSraEvent({ action: SraEventAction.BuyboxClickout, sponsoredAd: props.sponsoredAd });
	}
}

return { __sfc: true,GridBundleRedesignExp, CategoryOrder, props, t, gridMoreInfoVariant, getPromotedOffersWithJwtv, allPromotedOffers, hasPromotedOffers, promotedOffersV1, categoryTranslatables, categories, getOfferCategoryLabel, clickOutV4, getBundlesByCategory, getOffersByCategory, trackSraEvent, clickOut, GridOfferRowRedesignExp, GridOfferMoreInfoExp, GridPromotedOfferCardMoreInfoV1, BuyBoxTypeSwitcher }
}

})